import { trimLower } from "../../shared/utils/helper-methods";

// Types
export type UploadResponse<T = unknown> = {
  ok: boolean;
  status: number;
  headers: Headers;
  data?: T;
  text?: string;
};

export type PostMultipartOptions = {
  bearerToken: string;
  url: string;
  files: File[] | FileList; // <-- allow both
  jsonPartName?: string;
  jsonPayload?: unknown;
  filePartName?: string;
  fileNameOverride?: (file: File, index: number) => string; // <-- typed
  timeoutMs?: number;
};

// Implementation
export async function postMultipartWithBearerFileAPI<T = unknown>(
  opts: PostMultipartOptions
): Promise<UploadResponse<T>> {
  const {
    bearerToken,
    url,
    files,
    jsonPartName = "metadata",
    jsonPayload,
    filePartName = "files",
    fileNameOverride,
    timeoutMs = 120000,
  } = opts;

  const form = new FormData();

  console.log(jsonPayload);
  let payloadToSend = jsonPayload;


  // If carrier is UMR and an Excel file is being sent, use JHP instead
  if (
    payloadToSend &&
    typeof payloadToSend === "object"
  ) {
    const payload = payloadToSend as any;
    const isLargeClaims = payload.dataType === 4;
    const hasExcelFile = Array.isArray(files)
      ? files.some(f => /\.(xlsx|xlsm|xls)$/i.test(f.name))
      : Array.from(files).some(f => /\.(xlsx|xlsm|xls)$/i.test(f.name));

    if (
      hasExcelFile && isLargeClaims &&
      (
        trimLower(payload.uploadCarrier) === "umr" ||
        trimLower(payload.uploadCarrier) === "united medical resources"
      )
    ) {
      payloadToSend = {
        ...payload,
        uploadCarrier: "JHP",
      };
    }
  }

  console.log(payloadToSend);
  if (payloadToSend !== undefined) {
    form.append(jsonPartName, JSON.stringify(payloadToSend));
  }

  // Helper to append one file consistently
  const appendOne = (f: File, i: number) => {
    const name = fileNameOverride ? fileNameOverride(f, i) : f.name;
    form.append(filePartName, f, name);
  };

  // Append files (robust guards)
  if (Array.isArray(files)) {
    files.forEach(appendOne);
  } else if (files && typeof (files as FileList).item === "function") {
    const fl = files as FileList;
    for (let i = 0; i < fl.length; i++) {
      const f = fl.item(i);
      if (f) appendOne(f, i);
    }
  } else {
    throw new Error("`files` must be a File[] or a FileList.");
  }

  // Abort/timeout
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, {
      method: "POST",
      body: form,
      headers: {
        // Do NOT set Content-Type; the browser will set proper multipart boundaries.
        Authorization: `Bearer ${bearerToken}`,
      },
      signal: controller.signal,
    });

    const headers = res.headers;
    const ct = (headers.get("content-type") || "").toLowerCase();

    let data: any | undefined;
    let text: string | undefined;

    if (ct.includes("application/json")) {
      try {
        data = await res.json();
      } catch {
        // ignore parse errors; leave data undefined and try text as fallback
      }
    }
    if (data === undefined) {
      try {
        text = await res.text();
      } catch {
        // ignore text parse errors
      }
    }

    return {
      ok: res.ok,
      status: res.status,
      headers,
      data,
      text,
    };
  } catch (err: any) {
    // Normalize aborted/timeout errors
    if (err?.name === "AbortError") {
      return {
        ok: false,
        status: 0,
        headers: new Headers(),
        text: "Request timed out or was aborted.",
      };
    }
    return {
      ok: false,
      status: 0,
      headers: new Headers(),
      text: String(err?.message || err),
    };
  } finally {
    clearTimeout(timer);
  }
}